import React, { useState,useEffect } from "react";
import { toast } from 'react-toastify';
import {format } from 'date-fns';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { getDefaultReviewList,getSingleReviewTemplateDetail,searchBookingReview } from "../../../../utils/reviewApi";
import { getSingleWidgetDetail } from "../../../../staticWidgets/utils/CommonWidgetApi";
import RatingStars from "../../../../components/RatingStars";
import ReviewPopup from "./ReviewPopup";
import BadgeWidget from "./BadgeWidget";
import FloatingWidget from "./FloatingWidget";

const CommonWidget = ({selectedLayout,isTemplateId,widgetId,reviewTabData}) => {
    const [apiData, setApiData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedReview, setSelectedReview] = useState(null);
    const [visibleGridCount, setVisibleGridCount] = useState(8);
    const [visibleListLayoutCount, setVisibleListLayoutCount] = useState(5);
    const [visibleMasonryLayoutCount, setVisibleMasonryLayoutCount] = useState(8);

    // Default values for the reviewTabData
    const {
        is_website_frontend,
        layout_is_full_width,
        business_name,
        layout_custom_width,
        style_background_color,
        header_is_widget_title,
        header_title,
        header_caption,
        setting_rating_format,
        style_widget_title_color,
        style_widget_title_fonts_title_size,
        style_widget_title_caption_color,
        style_widget_title_fonts_caption_size,
        style_header_background_color,
        header_is_show_header,
        header_write_reviews_button,
        header_source_icon,
        header_rating,
        header_number_of_reviews,
        style_header_text_color,
        style_header_write_review_background_color,
        style_header_write_review_text_color,
        header_write_review_button_type,
        style_rating_stars_color,
        layout_rows_on_mobile,
        layout_rows,
        style_load_more_button_color,
        style_load_more_button_text_color
    } = reviewTabData || {};

    // Map layout name to a specific class
    const layoutClassMap = {
        carousel_layout: 'upgadeify-carousel-style',
        list_layout: 'upgadeify-list-style',
        grid_layout: 'upgadeify-grid-style',
        masonry_layout: 'upgadeify-masonry-style',
        slider_layout: 'upgadeify-slider-style',
    };
    const layoutClass = layoutClassMap[selectedLayout] || '';

    //Helper to get slidesToShow from width
    const getSlidesToShow = (width) => {
        const w = Number(width);
        if (w >= 1024) return 3;
        if (w >= 768) return 2;
        return 1;
    };
    //State use for handle responsive logic
    const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(layout_custom_width));

    //Update slidesToShow when window resizes or width changes
    useEffect(() => {
        const handleResize = () => {
        setSlidesToShow(getSlidesToShow(layout_custom_width));
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [layout_custom_width]);

    //Carousel Slider Settings
    const carouselSliderSettings = {
        dots: false,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        arrows: true,
        autoplay: false,
        responsive: [
        {
            breakpoint: 768,
            settings: {
                slidesToShow: 1,
                slidesToScroll: 1,
                arrows: true,
                dots: false,
            },
        },
        ],
    };

    //Slider Settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShow,
        slidesToScroll: 3,
        arrows: false,
        autoplay: false,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: true,
                    dots: false,
                },
            },
        ],
    };

    // Fetching Review data
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                let result;
                if (isTemplateId) {
                    result = await getSingleReviewTemplateDetail(isTemplateId);
                } else if (widgetId) {
                    result = await getSingleWidgetDetail(widgetId);
                } else if (business_name) {
                    const payload = { business_name: business_name };
                    result = await searchBookingReview(payload);
                } else {
                    let app_type = 'Booking_Review';
                    result = await getDefaultReviewList(app_type);
                }
                setApiData(result); 
            } catch (error) {
                setApiData(null); 
                toast.warning(error.message);
            } finally {
                setLoading(false); 
            }
        };

        fetchData();
    }, [isTemplateId, widgetId, business_name]);

    //Handle Layout Rows and Columns
    const layoutColumns = 3;
    // Simple: detect if the screen is mobile once at render time
    const isMobile = window.innerWidth <= 768;
    const layoutRows = isMobile
        ? layout_rows_on_mobile
        : layout_rows;

    useEffect(() => {
        const newGridCount = layoutRows * layoutColumns;
        const newMasonryCount = layoutRows * layoutColumns;

        setVisibleGridCount(newGridCount);
        setVisibleListLayoutCount(layoutRows);
        setVisibleMasonryLayoutCount(newMasonryCount);
    }, [layoutRows, layoutColumns]);

    // Open popup when clicking "Read More"
    const handleReadMoreClick = (review) => {
        setSelectedReview(review);
    };

    // Close the popup
    const handleClosePopup = () => {
        setSelectedReview(null);
    };

    // Load More List Layout function
    const handleGridLoadMore = () => {
        setVisibleGridCount((prevCount) => prevCount + 8);
    };
    
    // Load List Layout More function
    const handleListLayoutLoadMore = () => {
        setVisibleListLayoutCount((prevCount) => prevCount + 5);
    };

    // Load Masonry Layout More function
    const handleMasonryLayoutLoadMore = () => {
        setVisibleMasonryLayoutCount((prevCount) => prevCount + 8);
    };

    //Handle dynamic styles based on layout
    const dynamicStyle =
        selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout'
            ? Number(layout_is_full_width) === 1
            ? {
                backgroundColor: style_background_color,
                margin: '0 auto',
            }
            : {
                backgroundColor: style_background_color,
                maxWidth: `${layout_custom_width}px`,
                margin: '0 auto',
                }
            : {};

    // Get grid layout style based on custom width
    const getGridLayoutStyle = () => {
        if (selectedLayout !== 'grid_layout') return {};

        if (layout_custom_width > 1280 && layout_custom_width < 1921) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(24%, 1fr))',
            
            };
        }

        if (layout_custom_width > 991 && layout_custom_width < 1280) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        
            };
        }

        if (layout_custom_width > 540 && layout_custom_width < 991) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(48%, 1fr))',
            
            };
        }

        if (layout_custom_width > 0 && layout_custom_width < 540) {
            return {
                gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
            
            };
        }

        // Default fallback
        return {
            gridTemplateColumns: 'repeat(auto-fill, minmax(32%, 1fr))',
        };
    };

    return (
        <div className={`upgadeify-review-template-widget upgadeify ${layoutClass} ${selectedReview ? 'carousel-popup-open' : ''} ${is_website_frontend}`} style={dynamicStyle}>
             {/* Loader and No Api Data Handling */}
            {loading ? (
                <div className="loader-container">
                    <div className="spinner"></div>
                    <p>Loading please wait...</p>
                </div>
            ) : apiData?.status === true ? (
            <>
                {/* Show Header Section Accordgin to condition*/}
                {selectedLayout !== 'badge_layout' && selectedLayout !== 'floating_badge_layout' && (
                    <>
                    {Number(header_is_widget_title) === 1 && (
                        <>
                            <div className="upgadeify-booking-main-title" style={{ color: style_widget_title_color,fontSize: style_widget_title_fonts_title_size }}>{header_title || ""}</div>
                            <div className="upgadeify-booking-main-caption" style={{ color: style_widget_title_caption_color,fontSize: style_widget_title_fonts_caption_size }}>{header_caption || ""}</div>
                        </>
                    )}
                    {Number(header_is_show_header) === 1 &&
                        (Number(header_source_icon) === 1 ||
                            Number(header_rating) === 1 ||
                            Number(header_number_of_reviews) === 1 ||
                            Number(header_write_reviews_button) === 1) && (
                            <div className="upgadeify-booking-reviews-header" style={{ backgroundColor: style_header_background_color,}}>
                        
                                <div className='upgadeify-booking-reviews-header-left'>
                                    {Number(header_source_icon) === 1 && (
                                    <div className='upgadeify-booking-reviews-header-title'>
                                       <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 113 36" className="injected-svg"><g fill="#003B95" clip-path="url(#a-170)"><path fill-rule="evenodd" d="M96.916 14.615c.02-.021.052-.021.072-.021.495.01.989-.03 1.473.02.484.052.988.32 1.112.917.01.072.04.134.051.206.02.072.031.123.113.03.278-.298.567-.586.906-.813a3.92 3.92 0 0 1 1.535-.576 3.853 3.853 0 0 1 1.142.02c.958.155 1.689.649 2.142 1.524.113.206.124.216.257.031a3.925 3.925 0 0 1 1.483-1.205 4.328 4.328 0 0 1 1.246-.36 4.561 4.561 0 0 1 1.184.01c1.153.165 1.987.752 2.409 1.874.165.443.247.896.299 1.37 0 .02 0 .04.02.061v7.166c-.494 0-.988.01-1.482-.01-.402-.01-.783-.103-1.051-.422a1.28 1.28 0 0 1-.288-.824c-.01-1.554-.01-3.11-.01-4.664a5.39 5.39 0 0 0-.103-1.143c-.103-.442-.371-.782-.937-.813-.855-.051-1.441.36-1.884 1.04-.268.401-.402.854-.494 1.318a4.63 4.63 0 0 0-.103 1.07v4.284c0 .123-.042.154-.155.154h-2.522c-.114 0-.155-.02-.155-.144 0-1.967.011-3.933 0-5.9 0-.39-.031-.793-.195-1.163-.227-.536-.68-.71-1.215-.68-.597.041-1.02.36-1.359.814-.309.412-.495.885-.608 1.39a5.997 5.997 0 0 0-.144 1.42v4.109c0 .123-.03.144-.154.144-.845-.01-1.679 0-2.523 0-.113 0-.154-.02-.154-.144.01-1.267 0-2.533 0-3.8 0-.04-.01-.092.01-.133.02.02.02.04.02.061v3.892c0 .02.031 0 .01 0 0 0-.01 0-.01-.01v-9.977c.052-.02.031-.072.062-.123Zm-50.862-4.448c.206.061.412.113.597.226.608.36.947 1.071.834 1.771-.123.69-.659 1.256-1.338 1.39a1.687 1.687 0 0 1-2.029-1.38 1.707 1.707 0 0 1 1.35-1.987c.02-.01.05 0 .071-.03.165.01.34.01.515.01ZM43.593 24.87c-.926-.021-1.832.03-2.738-.032-.505-.03-.886-.277-1.154-.7-.288-.463-.525-.968-.782-1.451-.32-.598-.638-1.195-.958-1.802a.512.512 0 0 0-.401-.278 1.664 1.664 0 0 0-.391-.02c-.206.01-.186-.032-.186.174v3.985c0 .082-.02.113-.113.113h-2.749c-.082 0-.113-.02-.103-.103.01-.134 0-.257 0-.391v-13.59c0-.207-.02-.176.175-.176.535.01 1.07-.02 1.617.02.617.042.988.32 1.122.855.051.175.062.37.062.546v5.807c0 .185 0 .185.195.185.155 0 .31 0 .464-.02a.703.703 0 0 0 .576-.34c.638-.999 1.287-1.998 1.936-3.007.04-.072.092-.102.175-.102h3.233c-.083.123-.155.236-.227.35-.844 1.245-1.678 2.502-2.522 3.747a3.814 3.814 0 0 1-.453.556c-.031.031-.072.062-.02.113.39.381.596.876.823 1.339.648 1.297 1.43 2.523 2.172 3.768.072.165.155.299.247.453Zm8.35-9.144c.175-.164.32-.329.495-.473.71-.587 1.523-.875 2.44-.916.628-.031 1.245.03 1.832.247.948.36 1.535 1.05 1.833 1.997a5.38 5.38 0 0 1 .237 1.617v2.872c0 1.205.01 2.41.01 3.624 0 .196.02.175-.175.165-.504-.01-.998.02-1.503-.02-.782-.052-1.236-.526-1.277-1.308-.03-.762-.01-1.514-.01-2.275v-2.564c0-.258-.02-.515-.093-.762-.144-.566-.504-.834-1.08-.855-1.154-.04-2.184.628-2.534 1.987-.082.33-.123.67-.123 1.01v4.622c0 .124-.02.165-.165.165-.885-.01-1.76-.01-2.646 0-.113 0-.144-.03-.144-.144V14.656c0-.052-.01-.103.082-.103.525.01 1.05-.031 1.575.02.577.062 1.071.35 1.205 1.05.02.042.031.073.041.104Zm32.34 1.565v.732c0 .082-.02.123-.113.113-.474 0-.937.01-1.41-.01-.505-.021-.886-.279-1.04-.814-.063-.216-.248-.309-.433-.37-.989-.34-2.183-.031-2.852.772a2.83 2.83 0 0 0-.577 1.184 3.454 3.454 0 0 0-.04 1.451c.174.865.637 1.514 1.44 1.905.608.288 1.246.37 1.895.268.649-.103 1.235-.391 1.76-.783.227-.175.443-.35.629-.566.051-.051.072-.062.113.01.37.628.751 1.246 1.132 1.874.042.072.021.124-.03.186-.35.401-.773.72-1.215.998a5.65 5.65 0 0 1-1.586.67 6.308 6.308 0 0 1-2.368.123 5.452 5.452 0 0 1-2.347-.834 4.968 4.968 0 0 1-1.998-2.491c-.257-.69-.37-1.4-.35-2.132.041-1.42.525-2.666 1.555-3.675a5.222 5.222 0 0 1 2.09-1.246 6.517 6.517 0 0 1 1.781-.299c.824-.02 1.627.103 2.389.422.566.237 1.06.567 1.348 1.133.145.268.206.546.206.844.01.186.02.36.02.535Zm-39.845 2.43v-5.024c0-.062-.021-.124.082-.124.535.01 1.07-.02 1.616.02.71.063 1.153.505 1.225 1.216.01.134.021.257.021.391v8.525c0 .113-.02.155-.144.155-.886-.01-1.76-.01-2.646 0-.124 0-.144-.041-.144-.155 0-1.668 0-3.336-.01-5.004.01 0 0 0 0 0Zm28.292 5.262c-.905.03-1.719-.742-1.719-1.72 0-.968.804-1.709 1.71-1.72.936-.01 1.73.794 1.73 1.72 0 .927-.773 1.75-1.72 1.72Zm-2.872-.628c-.03-.083-.01-.165-.01-.237v-9.37c0-.185 0-.185-.195-.185h-1.288c.042-.041.083-.02.134-.02h1.256c.093 0 .124.02.124.113 0 3.17 0 6.342-.01 9.513 0 .062-.01.124-.01.186Z" clip-rule="evenodd"></path><path fill-rule="evenodd" d="M96.917 14.615v10.234c-.042-.072-.031-.124-.031-.165V14.81c0-.072-.031-.144.03-.195Z" clip-rule="evenodd"></path><path d="M8.072 17.487c-.103-.051-.196-.113-.309-.175.072-.062.134-.113.196-.165.226-.195.443-.39.628-.628.546-.72.751-1.534.7-2.43-.072-1.276-.67-2.234-1.781-2.862-.783-.443-1.637-.597-2.523-.607a204.17 204.17 0 0 0-3.5 0c-.474 0-.865.195-1.164.566-.165.206-.247.443-.319.69v12.942h4.994c.7 0 1.39-.093 2.048-.33.979-.33 1.782-.895 2.296-1.812.505-.906.618-1.884.402-2.893-.196-1.02-.741-1.802-1.668-2.296Zm-5.323-2.47s.01 0 0 0c0-.392-.01-.773.01-1.164.02-.567.278-.855.845-.886.545-.03 1.091-.051 1.637.02.68.104 1.122.526 1.245 1.195.114.587.042 1.143-.34 1.627-.298.391-.73.535-1.204.546-.69.01-1.38 0-2.07 0-.092 0-.123-.031-.113-.124 0-.402-.01-.813-.01-1.215Zm3.892 6.743c-.35.443-.824.628-1.36.638-.813.02-1.616 0-2.429.01-.093 0-.103-.03-.103-.102v-1.462c0-.464-.01-.927 0-1.4.01-.577.33-.876.906-.876.515 0 1.02-.01 1.534 0 .66.02 1.215.237 1.565.824.433.67.381 1.75-.113 2.368Zm63.094-7.218c-.33.01-.649 0-.978 0-.134 0-.258-.01-.392.01-.535.011-.906.268-1.132.763a3.172 3.172 0 0 0-.649-.474c-.895-.505-1.863-.618-2.862-.484-1.73.237-2.914 1.215-3.532 2.832-.504 1.328-.525 2.697-.144 4.066.258.927.742 1.73 1.483 2.358.927.783 2.008 1.081 3.212 1.02.783-.042 1.504-.258 2.121-.773.02-.01.031-.03.062-.02.01.185 0 .36-.01.545-.072.71-.412 1.256-1.081 1.555-.721.33-1.483.309-2.245.196-.422-.062-.834-.206-1.235-.32-.505-.144-.845.01-1.071.485-.02.04-.041.092-.062.133-.175.443-.35.875-.525 1.318-.041.093-.02.124.062.175.762.412 1.565.67 2.409.814.247.04.484.072.731.103h1.112c.02-.031.052-.021.072-.021.36-.02.71-.082 1.05-.175.917-.227 1.75-.618 2.43-1.287.844-.834 1.225-1.853 1.287-3.017v-9.678c.02-.093-.01-.124-.113-.124Zm-2.965 6.322c-.278.762-.896 1.205-1.72 1.215-1.02.01-1.73-.484-2.049-1.452-.278-.854-.288-1.709.041-2.543.299-.751.876-1.132 1.679-1.163.422-.01.844.03 1.225.226.535.268.793.742.927 1.298.082.34.102.69.113 1.06 0 .433-.052.906-.216 1.36Zm28.931-2.554a4.511 4.511 0 0 0-.473-1.235 4.949 4.949 0 0 0-1.967-2.018 5.487 5.487 0 0 0-2.152-.669 6.037 6.037 0 0 0-1.554.03 5.341 5.341 0 0 0-1.586.485 4.989 4.989 0 0 0-2.07 1.822c-.576.906-.834 1.905-.823 2.976.01.267.01.535.051.793.083.586.247 1.142.505 1.678a4.788 4.788 0 0 0 1.668 1.925c.782.535 1.657.824 2.594.906a5.96 5.96 0 0 0 2.718-.37 4.968 4.968 0 0 0 2.132-1.576 4.98 4.98 0 0 0 .875-1.688 6.22 6.22 0 0 0 .226-1.236 6.013 6.013 0 0 0-.144-1.822Zm-2.738 1.298c0 .762-.145 1.39-.546 1.936-.33.463-.772.762-1.328.885-.567.134-1.112.103-1.638-.134-.535-.237-.916-.638-1.163-1.163a3.273 3.273 0 0 1-.32-1.555c.032-.793.248-1.524.845-2.09.536-.504 1.184-.69 1.915-.618 1.04.114 1.689.71 2.05 1.658.143.391.205.782.184 1.081Zm-62.837-4.674c-1.41-.69-2.883-.762-4.366-.289-1.523.484-2.594 1.493-3.181 2.986-.412 1.04-.474 2.121-.257 3.223.247 1.266.875 2.317 1.904 3.099.989.752 2.121 1.07 3.347 1.07a5.67 5.67 0 0 0 1.38-.154c1.564-.38 2.748-1.266 3.469-2.718.577-1.174.69-2.43.422-3.706-.35-1.596-1.246-2.79-2.718-3.511Zm-.041 4.777c0 .71-.155 1.37-.608 1.936-.514.648-1.215.906-2.028.875-1.153-.052-2.028-.865-2.275-2.07-.175-.865-.093-1.699.38-2.47.526-.845 1.4-1.226 2.379-1.092 1.091.154 1.73.824 2.039 1.853a3.4 3.4 0 0 1 .113.968Zm-11.46-4.767c-1.266-.638-2.604-.741-3.963-.422-1.627.391-2.811 1.349-3.501 2.883-.463 1.019-.566 2.09-.402 3.191.175 1.215.7 2.255 1.607 3.079 1.05.947 2.306 1.349 3.696 1.36.545 0 1.07-.063 1.596-.207 1.503-.432 2.625-1.328 3.294-2.749.525-1.132.639-2.317.392-3.531-.33-1.637-1.226-2.863-2.719-3.604Zm-.442 6.363c-.402.741-1.05 1.133-1.884 1.205-1.35.123-2.338-.742-2.626-1.988a2.817 2.817 0 0 1-.051-.288 2.338 2.338 0 0 1-.031-.535c0-.731.165-1.4.638-1.977.566-.69 1.318-.937 2.193-.824.978.124 1.627.7 1.956 1.617.32.937.289 1.884-.195 2.79Z"></path></g><defs><clipPath id="a-170"><path fill="#fff" d="M0 0h112.36v36H0z"></path></clipPath></defs></svg>
                                        <div className='upgadeify-booking-reviews-header-title-text' style={{color: style_header_text_color,}}>Reviews</div>
                                    </div>
                                    )}
                                    <div className='upgadeify-booking-reviews-header-rating'>
                                        {Number(header_rating) === 1 && (
                                        <>
                                            <div className="upgadeify-booking-reviews-header-rating-text" style={{color: style_header_text_color,}}>{setting_rating_format === '4.9' ? Number(apiData.data.average_rating).toFixed(1) : Number(apiData.data.average_rating).toFixed(2) }</div>
                                            <div className="upgadeify-booking-reviews-item-rating">
                                                <RatingStars average_rating={apiData.data.average_rating} newColor={style_rating_stars_color} />
                                            </div>
                                        </>
                                        )}
                                        {Number(header_number_of_reviews) === 1 && (
                                            <span className="upgadeify-booking-reviews-header-number-of-reviews-text">({apiData.data.total_reviews})</span>
                                        )}
                                    </div>
                                </div>
                                {Number(header_write_reviews_button) === 1 && (
                                    <div className='upgadeify-booking-reviews-header-right'>
                                        <a
                                            href={apiData?.data?.google_url?.startsWith('http') ? apiData.data.google_url : `https://${apiData.data.google_url}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className='upgadeify-booking-reviews-header-button'
                                            style={{color: style_header_write_review_text_color,backgroundColor: style_header_write_review_background_color,fontSize: header_write_review_button_type}}>Review us on Booking
                                        </a>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/*If Carousel Layout Type*/}
                {selectedLayout === 'carousel_layout' && (
                    <Slider {...carouselSliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                        ))}
                    </Slider>
                )}

                {/*If List Layout Type*/}
                {selectedLayout === 'list_layout' && (
                    <>
                        <div className='upgadeify-list_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleListLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {/* Load More Button  */}
                        {visibleListLayoutCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleListLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Grid Layout Type*/}
                {selectedLayout === 'grid_layout' && (
                    <>
                        <div className='upgadeify-grid-layout' style={getGridLayoutStyle()}>
                            {apiData.data.review_list.slice(0, visibleGridCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                        </div>
                        {visibleGridCount < apiData.data.review_list.length && (
                            <div className="load-more-button">
                                <button onClick={handleGridLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                            </div>
                        )}
                    </>
                )}

                {/*If Masonry Layout Type*/}
                {selectedLayout === 'masonry_layout' && (
                    <>
                        <div className='upgadeify-masonry_layout-layout'>
                            {apiData.data.review_list.slice(0, visibleMasonryLayoutCount).map((review) => (
                                <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData}/>
                            ))}
                            
                        </div>
                            {/* Load More Button  */}
                            {visibleMasonryLayoutCount < apiData.data.review_list.length && (
                                <div className="load-more-button">
                                    <button onClick={handleMasonryLayoutLoadMore} style={{backgroundColor: style_load_more_button_color,color: style_load_more_button_text_color}}>Load More</button>
                                </div>
                            )}
                    </>
                )}

                {/*If Slider Layout Type*/}
                {selectedLayout === 'slider_layout' && (
                    <Slider {...sliderSettings}>
                        {apiData.data.review_list.map((review) => (
                            <ReviewLoopItem key={review.id} review={review} handleReadMoreClick={handleReadMoreClick} reviewTabData={reviewTabData} />
                        ))}
                    </Slider>
                )}

                {/*If Badge Layout Type*/}
                {selectedLayout === 'badge_layout' && (
                    <BadgeWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*If Floating Badge Layout Type*/}
                {selectedLayout === 'floating_badge_layout' && (
                    <FloatingWidget selectedLayout={selectedLayout} isTemplateId={isTemplateId} widgetId={widgetId} reviewTabData={reviewTabData} />
                )}

                {/*Common Popup */}
                {selectedReview && <ReviewPopup review={selectedReview} onClose={handleClosePopup} />}
            </>
            ) : (
                <div className="no-inner-result-found">
                    <p>No Booking reviews found please try.</p>
                </div>
            )}
        </div >
    );
};

const ReviewLoopItem = ({ review, handleReadMoreClick, reviewTabData }) => (
    <>
        <div className="upgadeify-booking-reviews">
            <div className='upgadeify-booking-reviews-item' style={{ padding: `${reviewTabData.layout_item_spacing}px`, backgroundColor: reviewTabData.style_review_background_color,}}>
                <div className='upgadeify-booking-reviews-head'>
                    {(Number(reviewTabData.review_is_show_reviewer_picture) === 1 || Number(reviewTabData.review_is_show_verified_badge) === 1) && (
                        <div className="upgadeify-booking-reviews-item-image">
                            {review.image ? (
                                <img src={review.image} alt="Review" />
                            ) : (
                                <div className="fallback-avatar">
                                    {review.name.charAt(0)}
                                </div>
                            )}
                           
                        </div>
                    )}
                    <div className='upgadeify-booking-reviews-item-name'>
                        {Number(reviewTabData.review_is_show_reviewer_name) === 1 && (
                        Number(reviewTabData.setting_is_enable_external_links) === 1 && review.author_url ? ( 
                                <a
                                    href={review.author_url} 
                                    target={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "_blank" : "_self"}
                                    rel={Number(reviewTabData.setting_is_open_links_in_new_tab) === 1 ? "noopener noreferrer" : undefined}
                                    style={{ color: reviewTabData.style_review_reviewer_name_color }}
                                >
                                    <span className="upgadeify-booking-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>{review.name}</span>
                                </a>
                            ) : (
                                <span className="upgadeify-booking-reviews-item-name-text" style={{ color: reviewTabData.style_review_reviewer_name_color }}>
                                    {review.name}
                                </span>
                            )
                        )}

                        {Number(reviewTabData.review_is_show_verified_badge) === 1 && (
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14" className="review-verified-badge-icon" aria-hidden="true">
                                <path fill={reviewTabData.style_review_verified_badge_color} d="M6.757.236a.35.35 0 0 1 .486 0l1.106 1.07a.35.35 0 0 0 .329.089l1.493-.375a.35.35 0 0 1 .422.244l.422 1.48a.35.35 0 0 0 .24.24l1.481.423a.35.35 0 0 1 .244.422l-.375 1.493a.35.35 0 0 0 .088.329l1.071 1.106a.35.35 0 0 1 0 .486l-1.07 1.106a.35.35 0 0 0-.089.329l.375 1.493a.35.35 0 0 1-.244.422l-1.48.422a.35.35 0 0 0-.24.24l-.423 1.481a.35.35 0 0 1-.422.244l-1.493-.375a.35.35 0 0 0-.329.088l-1.106 1.071a.35.35 0 0 1-.486 0l-1.106-1.07a.35.35 0 0 0-.329-.089l-1.493.375a.35.35 0 0 1-.422-.244l-.422-1.48a.35.35 0 0 0-.24-.24l-1.481-.423a.35.35 0 0 1-.244-.422l.375-1.493a.35.35 0 0 0-.088-.329L.236 7.243a.35.35 0 0 1 0-.486l1.07-1.106a.35.35 0 0 0 .089-.329L1.02 3.829a.35.35 0 0 1 .244-.422l1.48-.422a.35.35 0 0 0 .24-.24l.423-1.481a.35.35 0 0 1 .422-.244l1.493.375a.35.35 0 0 0 .329-.088L6.757.236Z"></path><path fill="#fff" fill-rule="evenodd" d="M9.065 4.85a.644.644 0 0 1 .899 0 .615.615 0 0 1 .053.823l-.053.059L6.48 9.15a.645.645 0 0 1-.84.052l-.06-.052-1.66-1.527a.616.616 0 0 1 0-.882.645.645 0 0 1 .84-.052l.06.052 1.21 1.086 3.034-2.978Z" clip-rule="evenodd"></path>
                            </svg>
                        )}

                        <span className="upgadeify-booking-reviews-item-date">
                            {Number(reviewTabData.review_is_show_date) === 1 && (
                                <span style={{color: reviewTabData.style_review_date_color,}}>{review.review_date ? format(new Date(review.review_date), 'yyyy-MM-dd') : ''}</span>
                            )}
                        </span>
                    </div>
                </div>
                <div className="upgadeify-booking-reviews-item-content">
                   {Number(reviewTabData.review_is_show_rating) === 1 && (
                        <span className="upgadeify-booking-reviews-item-rating" style={{ fontSize: reviewTabData.style_review_rating_size }}>
                            <RatingStars average_rating={review.rating} newColor={reviewTabData.style_rating_stars_color}/>
                        </span>
                    )}
                    <div className="upgadeify-booking-reviews-item-text" style={{color: `${reviewTabData.style_review_text_color}`,fontSize: reviewTabData.style_review_text_font_size}}>
                        {review.description.length > 50
                        ? `${review.description.slice(0, 50)}...`
                        : review.description}
                    </div>
                    {review.description.length > 50 && (
                        <div className='upgadeify-booking-reviews-readmore' onClick={() => handleReadMoreClick(review)} style={{color: reviewTabData.style_read_more_color,fontSize: reviewTabData.style_review_text_font_size}}>
                                Read more
                        </div>
                    )}

                    {Number(reviewTabData.review_is_show_review_source) === 1 && (  
                        <div className='upgadeify-booking-reviews-item-image-badge'>
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="injected-svg"><path fill="#003B95" d="M2 4.222C2 2.995 2.995 2 4.222 2h15.556C21.005 2 22 2.995 22 4.222v15.556A2.222 2.222 0 0 1 19.778 22H2V4.222Z"></path><path fill="#fff" d="M9.32 7.732a.91.91 0 0 1 .629-.23c.688 0 1.377-.006 2.065.002.712.007 1.463.195 1.998.71.427.4.653 1.001.649 1.595.012.49-.121.984-.399 1.38-.148.217-.346.387-.54.557.191.113.39.213.556.365.434.383.678.966.714 1.551.044.602-.093 1.232-.446 1.72-.353.496-.892.82-1.46.974-.357.103-.728.13-1.097.144H9V8.471a.981.981 0 0 1 .32-.74Zm1.464 1.446c-.098.134-.113.309-.114.471v1.49h1.287c.203 0 .413-.044.585-.162.248-.166.385-.468.419-.765.032-.306-.013-.64-.206-.884-.181-.236-.478-.343-.762-.344a54.164 54.164 0 0 0-.76 0c-.165 0-.344.053-.45.194Zm.121 3.43c-.182.105-.235.34-.234.542-.003.605-.001 1.21-.002 1.815h1.514c.358-.002.73-.168.922-.495.2-.342.226-.775.122-1.154a1.04 1.04 0 0 0-.635-.703c-.326-.127-.678-.068-1.017-.08-.223.014-.466-.05-.67.075Z"></path><circle cx="17" cy="15.5" r="1" fill="#fff"></circle></svg>
                            Posted On   
                            <> <a href={`https://www.booking.com`} target="blank">Booking</a></> 
                        </div> 
                    )} 
                </div>
            </div>
        </div>
    </>
);

export default CommonWidget;
